import { FC, useMemo } from 'react';
import { ScanModel } from '../../network';
import { BentMesh } from '../3dModels/BentMesh';
import { StandingMesh } from '../Standing/StandingMesh';
import { BentDeviationMesh } from '../3dModels/BentDeviationMesh';
import { CenterOfMass } from '../Sagittal/CenterOfMass';
import { ThreeDPlane } from '../3dModels/ThreeDPlane';
import { HipToEar } from '../Sagittal/HipToEar';
import { KneeBend } from '../Sagittal/KneeBend';
import { AnkleFlexion } from '../Sagittal/AnkleFlexion';
import { SurfaceSpineSpline } from '../3dModels/SurfaceSpineSpline';
import { LowerSagittal } from '../Sagittal/LowerSagittal';
import { UpperSagittal } from '../Sagittal/UpperSagittal';
import { SagittalBalancePlane } from '../Sagittal/SagittalBalancePlane';
import { ShoulderAsymmetry } from '../Standing/ShoulderAsymmetry';
import { MetricKeyEnum } from '../LayerControls/layerStore';
import { MidSagittalPlane } from '../Sagittal/MidSagittalPlane';

interface ModelRendererProps {
  scan: ScanModel;
  accessToken: string;
}
export const ModelRenderer: FC<ModelRendererProps> = ({
  scan,
  accessToken,
}) => {
  const standingMeshResults = useMemo(() => {
    const modelUrl = scan.standing_mesh_stream;
    const textureUrl = scan.standing_mesh_texture_stream;

    if (!modelUrl || !textureUrl) {
      return null;
    }

    return {
      modelUrl,
      textureUrl,
    };
  }, [scan]);

  const bentMeshResults = useMemo(() => {
    const bentDeviationMeshUrl =
      scan.featureset_values?.[0]?.bent_deviation_mesh_stream;
    const modelUrl = scan.bent_mesh_stream;
    const textureUrl = scan.bent_mesh_texture_stream;

    if (!bentDeviationMeshUrl || !modelUrl || !textureUrl) {
      return null;
    }

    return {
      modelUrl,
      textureUrl,
      bentDeviationMeshUrl,
    };
  }, [scan]);

  const featureSet = scan.featureset_values?.[0];

  const centerOfMass = featureSet?.center_of_mass;
  const centerOfMassAboveHips = featureSet?.center_of_mass_above_hips;

  const trunkShift = featureSet?.mesh_metadata?.trunk_plane;
  const plumbShift = featureSet?.mesh_metadata?.plumb_shift_plane;
  const midSacPlane = featureSet?.mesh_metadata?.msl_plane;
  const hipCentroid = featureSet?.sagittal_balance_plane || 0;

  return (
    <>
      {bentMeshResults && (
        <BentMesh
          modelUrl={bentMeshResults.modelUrl}
          textureUrl={bentMeshResults.textureUrl}
          accessToken={accessToken}
          textureEnabled={scan.textures_enabled}
        />
      )}
      {standingMeshResults && (
        <StandingMesh
          modelUrl={standingMeshResults.modelUrl}
          textureUrl={standingMeshResults.textureUrl}
          textureEnabled={scan.textures_enabled}
          accessToken={accessToken}
        />
      )}
      {bentMeshResults && (
        <BentDeviationMesh
          bentDeviationMeshUrl={bentMeshResults.bentDeviationMeshUrl}
          accessToken={accessToken}
        />
      )}

      <ShoulderAsymmetry featuresetValues={featureSet} />

      {centerOfMass && (
        <CenterOfMass
          position={centerOfMass}
          layerKey={MetricKeyEnum.CenterOfMass}
          color='red'
          alwaysOnTop={true}
          size={0.02}
          opacity={1}
        />
      )}
      {centerOfMassAboveHips && (
        <CenterOfMass
          position={centerOfMassAboveHips}
          layerKey={MetricKeyEnum.CenterOfMassAboveHips}
          color='orange'
          alwaysOnTop={true}
          size={0.02}
          opacity={1}
        />
      )}

      {trunkShift && (
        <ThreeDPlane
          layerKey={MetricKeyEnum.TrunkShift}
          color='orange'
          hipCentroid={hipCentroid}
          value={trunkShift}
        />
      )}

      {plumbShift && (
        <ThreeDPlane
          layerKey={MetricKeyEnum.PlumbShift}
          color='red'
          hipCentroid={hipCentroid}
          value={plumbShift}
        />
      )}

      {midSacPlane && (
        <ThreeDPlane
          layerKey={MetricKeyEnum.MidSacralPlane}
          color='blue'
          hipCentroid={hipCentroid}
          value={midSacPlane}
        />
      )}
      <HipToEar featuresetValues={featureSet} />
      <KneeBend featuresetValues={featureSet} />
      <AnkleFlexion featuresetValues={featureSet} />
      <SurfaceSpineSpline featuresetValues={featureSet} />
      <LowerSagittal featuresetValues={featureSet} />
      <UpperSagittal featuresetValues={featureSet} />
      <SagittalBalancePlane featuresetValues={featureSet} />
      <MidSagittalPlane featuresetValues={featureSet} />
    </>
  );
};
